<script setup lang="ts">
  import { NIcon } from 'naive-ui'
  import { LayoutList } from '@vicons/tabler'

  type AdminUser = {
    id: number
    username: string
    role: string
    displayName: string | null
    gender: number
  }

  const WEEKDAY_META = [
    { label: '星期日', emoji: '🌞' },
    { label: '星期一', emoji: '💼' },
    { label: '星期二', emoji: '📚' },
    { label: '星期三', emoji: '🪴' },
    { label: '星期四', emoji: '☕' },
    { label: '星期五', emoji: '🎉' },
    { label: '星期六', emoji: '🎮' },
  ] as const

  const collapsed = defineModel<boolean>('collapsed', { default: false })
  const pinned = ref(false)

  const { data: meData } = await useFetch<{ ok: boolean; user: AdminUser }>('/api/auth/me')

  const user = computed(() => meData.value?.user ?? null)

  const displayName = computed(() => user.value?.displayName || user.value?.username || '未登录')
  const roleLabel = computed(() => getAdminRoleLabel(user.value?.role))

  const avatarSrc = computed(() => getAdminUserAvatarSrc(user.value?.gender))

  const todayInfo = computed(() => {
    const now = new Date()
    const weekday = WEEKDAY_META[now.getDay()]!
    const dateText = `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`
    return {
      dateText,
      dateEmoji: '📅',
      weekdayLabel: weekday.label,
      weekdayEmoji: weekday.emoji,
    }
  })

  function toggle() {
    collapsed.value = !collapsed.value
  }

  function onScroll() {
    pinned.value = window.scrollY > 0
  }

  onMounted(() => {
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
  })
</script>

<template>
  <header
    class="admin-header"
    :class="{
      'admin-header--collapsed': collapsed,
      'admin-header--pinned': pinned,
    }"
  >
    <div class="flex flex-col items-center" @click="toggle">
      <div
        class="flex items-center justify-center w-10 h-10 hover:bg-gray-200 hover:rounded-full transition-all duration-300 cursor-pointer"
      >
        <NIcon :component="LayoutList" :size="24" color="#333" />
      </div>
    </div>

    <div class="admin-header__user">
      <div class="admin-header__date" :title="`${todayInfo.dateText} ${todayInfo.weekdayLabel}`">
        <span>今天是</span>
        <span class="admin-header__date-item">
          <span class="admin-header__emoji" aria-hidden="true">{{ todayInfo.dateEmoji }}</span>
          <span>{{ todayInfo.dateText }}</span>
        </span>
        <span class="admin-header__date-sep">·</span>
        <span class="admin-header__date-item">
          <span class="admin-header__emoji" aria-hidden="true">{{ todayInfo.weekdayEmoji }}</span>
          <span>{{ todayInfo.weekdayLabel }}</span>
        </span>
      </div>

      <n-avatar round :size="40" :src="avatarSrc" />
      <div class="admin-header__meta">
        <div class="admin-header__name" :title="displayName">{{ displayName }}</div>
        <div class="admin-header__role">{{ roleLabel }}</div>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
  .admin-header {
    position: fixed;
    top: 20px;
    right: 20px;
    left: calc(20px + 270px + 20px);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 75px;
    padding: 0 20px;
    background: #fff;
    border-bottom: 1px solid #eef0f3;
    border-radius: 18px;
    transition: top 0.2s ease, left 0.2s ease, border-radius 0.2s ease;
  }

  .admin-header--collapsed {
    left: calc(20px + 64px + 20px);
  }

  .admin-header--pinned {
    top: 0;
    border-radius: 0 0 18px 18px;
  }

  .admin-header__date {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 0 12px;
    font-size: 14px;
    color: #4b5563;
    white-space: nowrap;
  }

  .admin-header__date-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .admin-header__emoji {
    font-size: 16px;
    line-height: 1;
  }

  .admin-header__date-sep {
    color: #d1d5db;
  }

  .admin-header__user {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .admin-header__meta {
    min-width: 0;
  }

  .admin-header__name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }

  .admin-header__role {
    margin-top: 2px;
    font-size: 12px;
    color: #8b93a7;
    line-height: 1.3;
  }
</style>
